import { Component, EventEmitter, inject, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatInputModule } from '@angular/material/input';
import { MatSelectChange, MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatOptionModule } from '@angular/material/core';
import { ReactiveFormsModule } from '@angular/forms';
import { Messages } from '@config/messages';
import { Subscription } from 'rxjs';
import {
  EnumTableDynamicInputType,
  formatSignerAllowedActions,
  parseSignerAllowedActions,
  SIGNER_ALLOWED_ACTIONS_EXCLUSIVE,
} from '@modals/app.modal';
import { TableService } from '@services/table.service';
import { buildCountryOptions, CountryOption, handleCountrySelectPanelOpened } from '@config/country-options';
import { LanguageService } from '@services/language.service';

@Component({
  selector: 'kzn-table-dynamic-input',
  standalone: true,
  imports: [CommonModule,
    MatInputModule,
    MatSelectModule,
    MatFormFieldModule,
    MatOptionModule,
    ReactiveFormsModule],
  templateUrl: './table-dynamic-input.component.html',
  styleUrl: './table-dynamic-input.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class TableDynamicInputComponent implements OnInit, OnChanges, OnDestroy {
  @Input() type: EnumTableDynamicInputType = EnumTableDynamicInputType.TEXT;
  @Input() label: string = '';
  @Input() options: string[] = [];
  @Input() value: string = '';
  @Input() required: boolean = true;
  @Input() placeholder: string | null = null;
  @Input() selectPlaceholder: string | null = null;
  @Input() optionLabels: Record<string, string> | null = null;
  @Input() searchPlaceholder: string | null = null;
  /** When true (reorderable table), only inputs stop drag — row can be dragged from cell chrome. */
  @Input() stopDragOnControls = false;
  @Input() readOnly = false;
  @Output() onTableDynamicInputValueUpdate = new EventEmitter<string>();

  EnumTableDynamicInputType = EnumTableDynamicInputType;

  onControlMouseDown(event: MouseEvent): void {
    if (this.stopDragOnControls) {
      event.stopPropagation();
    }
  }

  private _countryCode: string = '';
  public countryOptions: CountryOption[] = [];
  public filteredCountryOptions: CountryOption[] = [];
  public selectedCountry: CountryOption = {
    name: "",
    iso2: "",
    dialCode: "",
    flag: ""
  };
  public countrySearchControl = new FormControl("");
  private _tableService = inject(TableService);
  private _languageService = inject(LanguageService);
  private _lastMultiSelection: string[] = [];
  Messages = Messages;
  form!: FormGroup;
  controlValueChangeSubscription!: Subscription;
  countrySearchSubscription!: Subscription;
  touchSubscription!: Subscription;

  get control() {
    return this.form.get('control') as FormControl;
  }

  t(key: string, params?: Record<string, string>): string {
    return this._languageService.instant(key, params);
  }

  /** Placeholder uses i18n key when provided; otherwise "Enter {label}". */
  placeholderText(): string {
    if (this.placeholder) return this.t(this.placeholder);
    return this.t("enterField", { field: this.t(this.label) });
  }

  selectPlaceholderText(): string {
    if (this.selectPlaceholder) return this.t(this.selectPlaceholder);
    return this.t("selectField", { field: this.t(this.label) });
  }

  searchPlaceholderText(): string {
    return this.searchPlaceholder ? this.t(this.searchPlaceholder) : this.t("search");
  }

  optionLabel(option: string): string {
    const key = this.optionLabels?.[option];
    if (key) return this.t(key);
    return option;
  }

  multiSelectTriggerLabel(selected: string[] | null | undefined): string {
    const values = selected ?? [];
    if (!values.length) return this.selectPlaceholderText();
    return values.map((item) => this.optionLabel(item)).join(", ");
  }

  private static readonly EMAIL_REGEX = "^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$";

  private parsePhoneParts(phone: string | null | undefined): { dialCode: string; number: string } {
    const raw = (phone || '').toString().trim();
    const m = raw.match(/^(\+\d{1,4})[-\s]?(.*)$/);
    if (m) return { dialCode: (m[1] || '').trim(), number: (m[2] || '').trim() };
    return { dialCode: '', number: raw };
  }

  ngOnInit() {
    this.initCountryOptions();
    const initialValue = this.resolveInitialControlValue();

    const validators = [
      ...(this.required ? [Validators.required] : []),
     ...(this.type === EnumTableDynamicInputType.EMAIL
   ? [Validators.pattern(TableDynamicInputComponent.EMAIL_REGEX)]
   : []),
    ];

    this.form = new FormGroup({
      control: new FormControl(initialValue, validators),
    });

    if (this.readOnly) {
      this.control.disable({ emitEvent: false });
    }

    if (this.type === EnumTableDynamicInputType.MULTI_DROPDOWN) {
      this._lastMultiSelection = [...(initialValue as string[])];
    }

    this.controlValueChangeSubscription = this.control?.valueChanges.subscribe((value) => {
      if (this.type === EnumTableDynamicInputType.MULTI_DROPDOWN) {
        return;
      }

      let controlValue = value;
      if (this.type === EnumTableDynamicInputType.TEL) {
        const localNumber = (controlValue || '').toString().trim();
        if (!localNumber) {
          this.onTableDynamicInputValueUpdate.emit('');
          return;
        }
        this.onTableDynamicInputValueUpdate.emit(this._tableService.getFormattedNumber(localNumber, this._countryCode));
        return;
      }
      this.onTableDynamicInputValueUpdate.emit((controlValue ?? '').toString());
    });

    this.touchSubscription = this._tableService.touchInputs$.subscribe(() => {
      this.control?.markAsTouched();
      this.control?.updateValueAndValidity({ emitEvent: false });
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes["options"] &&
      !changes["options"].firstChange &&
      this.control &&
      this.type === EnumTableDynamicInputType.MULTI_DROPDOWN
    ) {
      const prevOptions = (changes["options"].previousValue as string[] | undefined) ?? [];
      const nextOptions = (changes["options"].currentValue as string[] | undefined) ?? [];
      if (!this.areSameStringArrays(prevOptions, nextOptions)) {
        this.syncMultiDropdownToOptions(false);
      }
    }

    if (!changes["value"] || changes["value"].firstChange || !this.control) {
      return;
    }

    const nextValue = this.resolveInitialControlValue();
    this.control.setValue(nextValue, { emitEvent: false });

    if (this.type === EnumTableDynamicInputType.MULTI_DROPDOWN) {
      this._lastMultiSelection = [...(nextValue as string[])];
    }

    if (this.type === EnumTableDynamicInputType.TEL) {
      const parts = this.parsePhoneParts(this.value);
      const byDial = parts.dialCode
        ? this.countryOptions.find((c) => (c.dialCode || "").trim() === parts.dialCode)
        : undefined;
      const byIn = this.countryOptions.find((country) => country.iso2 === "IN");
      this.selectedCountry = byDial || byIn || this.countryOptions[0];
      this._countryCode = this.selectedCountry?.dialCode || parts.dialCode || "";
    }
  }

  onMultiDropdownChange(event: MatSelectChange): void {
    const selected = Array.isArray(event.value) ? [...event.value] : [];
    const normalized = this.normalizeMultiDropdownSelection(selected, this._lastMultiSelection);
    this._lastMultiSelection = normalized;
    this.control.setValue(normalized, { emitEvent: false });
    this.onTableDynamicInputValueUpdate.emit(formatSignerAllowedActions(normalized));
  }

  private normalizeMultiDropdownSelection(selected: string[], previous: string[]): string[] {
    const allowed = new Set(this.options);
    const exclusive = new Set<string>(SIGNER_ALLOWED_ACTIONS_EXCLUSIVE);
    const clean = selected.filter((item) => allowed.has(item));
    const added = clean.filter((item) => !previous.includes(item));

    if (added.some((item) => exclusive.has(item))) {
      const lastExclusive = added.find((item) => exclusive.has(item));
      return lastExclusive ? [lastExclusive] : this.defaultMultiDropdownSelection(allowed);
    }

    const exclusiveOnly = clean.filter((item) => exclusive.has(item));
    if (exclusiveOnly.length === 1 && clean.length === 1) {
      return [exclusiveOnly[0]];
    }

    const tagsOnly = clean.filter((item) => !exclusive.has(item));
    return tagsOnly.length ? tagsOnly : this.defaultMultiDropdownSelection(allowed);
  }

  private areSameStringArrays(a: readonly string[], b: readonly string[]): boolean {
    if (a.length !== b.length) return false;
    return a.every((value, index) => value === b[index]);
  }

  private defaultMultiDropdownSelection(allowed: Set<string>): string[] {
    if (allowed.has("All")) return ["All"];
    const first = this.options.find((item) => allowed.has(item));
    return first ? [first] : [];
  }

  private syncMultiDropdownToOptions(emitUpdate: boolean): void {
    const allowed = new Set(this.options);
    const current = Array.isArray(this.control.value) ? [...this.control.value] : [];
    const filtered = current.filter((item) => allowed.has(item));
    const next = filtered.length
      ? this.normalizeMultiDropdownSelection(filtered, filtered)
      : this.defaultMultiDropdownSelection(allowed);

    this._lastMultiSelection = [...next];
    this.control.setValue(next, { emitEvent: false });

    if (emitUpdate) {
      this.onTableDynamicInputValueUpdate.emit(formatSignerAllowedActions(next));
    }
  }

  private resolveInitialControlValue(): string | string[] {
    if (this.type === EnumTableDynamicInputType.MULTI_DROPDOWN) {
      return parseSignerAllowedActions(this.value);
    }
    if (this.type === EnumTableDynamicInputType.TEL) {
      return this.parsePhoneParts(this.value).number;
    }
    return this.value;
  }

  private initCountryOptions(): void {
    if (this.type !== EnumTableDynamicInputType.TEL) return;
    this.countryOptions = buildCountryOptions("en");
    this.filteredCountryOptions = [...this.countryOptions];
    const parts = this.parsePhoneParts(this.value);
    const byDial = parts.dialCode ? this.countryOptions.find((c) => (c.dialCode || '').trim() === parts.dialCode) : undefined;
    const byIn = this.countryOptions.find((country) => country.iso2 === "IN");
    this.selectedCountry = byDial || byIn || this.countryOptions[0];
    this._countryCode = this.selectedCountry?.dialCode || parts.dialCode || "";

    this.countrySearchSubscription = this.countrySearchControl.valueChanges.subscribe((term) => {
      const value = (term || "").toString().toLowerCase().trim();
      if (!value) {
        this.filteredCountryOptions = [...this.countryOptions];
        return;
      }
      this.filteredCountryOptions = this.countryOptions.filter((country) => {
        return (
          country.name.toLowerCase().includes(value) ||
          country.dialCode.includes(value) ||
          country.iso2.toLowerCase().includes(value)
        );
      });
    });
  }

  onCountrySelect(country: CountryOption): void {
    this.selectedCountry = country;
    this._countryCode = country?.dialCode || '';
    if (this.type === EnumTableDynamicInputType.TEL) {
      const local = (this.control?.value || '').toString().trim();
      if (local) this.onTableDynamicInputValueUpdate.emit(this._tableService.getFormattedNumber(local, this._countryCode));
    }
  }

  onCountryPanelOpened(opened: boolean): void {
    handleCountrySelectPanelOpened(opened, () => this.countrySearchControl.setValue('', { emitEvent: true }));
  }

  ngOnDestroy(): void {
    this.controlValueChangeSubscription?.unsubscribe();
    this.countrySearchSubscription?.unsubscribe();
    this.touchSubscription?.unsubscribe();
  }
}
